import React from 'react';
import moment from 'moment/moment';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { LoadingOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { ArrowLeftIcon } from 'components/common/icons';
import { ValidationErrors } from 'constants/validations';
import { ContactPersonAddEdit } from 'components/common/contactPerson/addEdit';
import { FeesAndCharges } from 'feature/purchaseRequestTabs/supplierOffers/edit/feesAndCharges';
import { PublishSupplierOfferModal } from 'pages/admin/supplierOffers/edit/publishSupplierOffer';
import { Button, Col, Collapse, Divider, Form, HasAccess, Row, Spin, Typography, Upload } from 'components';
import { GeneralInformation } from 'feature/purchaseRequestTabs/supplierOffers/edit/generalInformation';
import { ShippingInformation } from 'feature/purchaseRequestTabs/supplierOffers/edit/shippingInformation';
import { SupplierOfferItemsTable } from 'feature/purchaseRequestTabs/supplierOffers/edit/SupplierOfferItems';
import styles from './../styles.module.scss';
import { useEdit } from './useEdit';

const { Title, Text } = Typography;

export const SupplierOfferEdit = React.memo(() => {
  const {
    form,
    handleEmailUpload,
    emailPreview,
    purchaseRequest,
    attachments,
    handleFormChange,
    setAttachments,
    selectedSupplier,
    contactPersons,
    isLoadingContactPerson,
    handleFormItemBlur,
    handleFormItemChange,
    supplierOfferId,
    onAttachmentAdd,
    onAttachmentDelete,
    setOpenContactPersonModal,
    openContactPersonModal,
    itemList,
    globalShippingCost,
    leadType,
    visibleColumns,
    setVisibleColumns,
    supplierOffer,
    onDraft,
    onPublish,
    publishModalState,
    recordId,
    source,
    relatedEntityId,
    supplierOfferLoadingState,
  } = useEdit();
  return (
    <>
      <Spin spinning={supplierOfferLoadingState} indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}>
        <Row gutter={[0, 52]} style={{ padding: '20px 28px 20px 60px' }} className={'margin-t-20'}>
          <Col span={24}>
            <Form form={form} onValuesChange={handleFormChange} layout="vertical" requiredMark={false}>
              <Row gutter={[0, 24]}>
                <Col span={24}>
                  <Row align={'middle'} justify={'center'}>
                    <Col className={styles.back_btn}>
                      <Button
                        title={'Click to go back'}
                        onClick={() => history.back()}
                        type={'link'}
                        icon={<ArrowLeftIcon />}
                      />
                    </Col>
                    <Title level={2} className={'reset-title font-weight-800 center'}>
                      Update Supplier Offer
                    </Title>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row gutter={[0, 24]}>
                    <Col span={24}>
                      <Form.Item
                        name="emailFile"
                        dependencies={['source']}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const source = getFieldValue('source');
                              if (source === 'EMAIL' && (!value || value.length === 0)) {
                                return Promise.reject(ValidationErrors.required('Email'));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Upload.Dragger
                          disabled={purchaseRequest?.isFinalStatus}
                          accept=".eml"
                          className={'email-upload'}
                          beforeUpload={handleEmailUpload}
                          maxCount={1}
                          showUploadList={false}
                          style={{ marginBottom: '16px' }}
                        >
                          <Row style={{ padding: '34px' }} justify={'center'}>
                            <Text className={'font-size-18 font-weight-700'}>Upload Email</Text>
                          </Row>
                        </Upload.Dragger>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      {emailPreview && (
                        <Collapse
                          className={styles.collapse}
                          accordion
                          expandIconPosition="end"
                          items={[
                            {
                              key: 'supplier-email',
                              label: 'Supplier Email',
                              children: (
                                <Row gutter={[0, 32]}>
                                  <Col span={24}>
                                    <Row justify="space-between">
                                      <Col>
                                        <Row gutter={24}>
                                          <Col>
                                            <Text>
                                              <strong>Subject:</strong> <br />
                                              {emailPreview.subject}
                                            </Text>
                                          </Col>
                                          <Col>
                                            <Text>
                                              <strong>From:</strong>
                                              <br />
                                              {emailPreview.from}
                                            </Text>
                                          </Col>
                                        </Row>
                                      </Col>
                                      <Col>
                                        <Text>{moment(emailPreview.dateTime).format('DD.MM.YYYY HH:mm')}</Text>
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col span={24}>
                                    <iframe
                                      srcDoc={emailPreview.body?.replace(
                                        /<a\s+(?![^>]*target=)/g,
                                        '<a target="_blank" '
                                      )}
                                      className="html-content-iframe"
                                      sandbox="allow-popups"
                                    />
                                  </Col>
                                </Row>
                              ),
                            },
                          ]}
                        />
                      )}
                    </Col>
                    <Col span={24}>
                      <Row align={'middle'} className={'gap-16'}>
                        <Link to={`${ADMIN_ROUTES.client}/${purchaseRequest.client?.id}/general`} target={'_blank'}>
                          <Text
                            underline
                            type={'secondary'}
                            className={'font-weight-600 font-size-16'}
                            ellipsis={{ tooltip: true }}
                          >
                            {purchaseRequest.client?.name}
                          </Text>
                        </Link>
                        <Link to={`${ADMIN_ROUTES.leads.view}/${purchaseRequest.leadId}/general`} target={'_blank'}>
                          <Text underline type={'secondary'} className={'font-size-16'} ellipsis={{ tooltip: true }}>
                            Original Lead
                          </Text>
                        </Link>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={28}>
                        <Col span={12}>
                          <GeneralInformation
                            handleFormItemBlur={handleFormItemBlur}
                            handleFormItemChange={handleFormItemChange}
                            attachments={attachments}
                            setAttachments={setAttachments}
                            contactPersons={contactPersons}
                            isLoadingContactPerson={isLoadingContactPerson}
                            selectedSupplier={selectedSupplier}
                            setOpenContactPersonModal={setOpenContactPersonModal}
                            onAttachmentAdd={onAttachmentAdd}
                            onAttachmentDelete={onAttachmentDelete}
                            purchaseRequest={purchaseRequest}
                          />
                        </Col>
                        <Col span={12}>
                          <Row gutter={[0, 16]}>
                            <Col span={24}>
                              <Form.Item name="shippingInformation" noStyle>
                                <ShippingInformation
                                  handleFormItemBlur={handleFormItemBlur}
                                  handleFormItemChange={handleFormItemChange}
                                  form={form}
                                  leadType={leadType}
                                  purchaseRequest={purchaseRequest}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={24}>
                              <Form.Item name="fee" noStyle>
                                <FeesAndCharges
                                  handleFormItemBlur={handleFormItemBlur}
                                  purchaseRequest={purchaseRequest}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col span={24}>
            <SupplierOfferItemsTable
              originalForm={form}
              globalShippingCost={globalShippingCost}
              supplierOfferId={supplierOfferId}
              initialItems={itemList}
              setVisibleColumns={setVisibleColumns}
              visibleColumns={visibleColumns}
              purchaseRequest={purchaseRequest}
            />
          </Col>
          <Col span={24}>
            <Row gutter={[0, 40]}>
              {itemList.map((item: any) => {
                return Object.keys(item.alternativeSubstituteItem).map((key) => {
                  const _item = item.alternativeSubstituteItem[key] || [];
                  return (
                    <Col span={24} key={key}>
                      <SupplierOfferItemsTable
                        originalForm={form}
                        globalShippingCost={globalShippingCost}
                        subTable={true}
                        originalItemId={item.id}
                        subTableType={item.itemActionType}
                        subTableBrand={_item[0].brandName}
                        subTableBrandId={_item[0].brandId}
                        subTableItemNumber={item.itemNumber}
                        subTableSku={item.sku}
                        supplierOfferId={supplierOfferId}
                        initialItems={_item}
                        setVisibleColumns={setVisibleColumns}
                        visibleColumns={visibleColumns}
                        purchaseRequest={purchaseRequest}
                      />
                    </Col>
                  );
                });
              })}
            </Row>
          </Col>
          <Divider className={styles.divider} />
          <Col span={24}>
            <Row justify={'end'} gutter={8}>
              <Col flex={'160px'}>
                <Button
                  block
                  size="large"
                  type={'primary'}
                  disabled={purchaseRequest?.isFinalStatus || supplierOffer.published}
                  ghost
                  onClick={onDraft}
                >
                  Save Draft
                </Button>
              </Col>
              <HasAccess role={PERMISSIONS.purchaseRequests.supplierOffers.write}>
                <Col flex={'160px'}>
                  <Button
                    size={'large'}
                    block
                    type={'primary'}
                    disabled={purchaseRequest?.isFinalStatus || supplierOffer.published}
                    onClick={() => onPublish(supplierOffer.id)}
                  >
                    {supplierOffer.published ? 'Published' : 'Publish'}
                  </Button>
                </Col>
              </HasAccess>
            </Row>
          </Col>
        </Row>
      </Spin>
      <ContactPersonAddEdit
        entityName={'SUPPLIER'}
        modalState={openContactPersonModal}
        clientId={selectedSupplier}
        onModalStateChange={() => setOpenContactPersonModal((oState) => !oState)}
      />
      <PublishSupplierOfferModal
        openModal={publishModalState}
        onClose={onPublish}
        recordId={recordId}
        entityId={relatedEntityId}
        source={source}
      />
    </>
  );
});
